// Feedback recorder (~/.claude/FEEDBACK.md section 2). Installed from main.tsx before React
// renders, so errors that happen during the first render are already captured.

type Crumb = { t: number; kind: string; detail: string };
type ErrorEntry = { message: string; stack?: string; source?: string; level?: string };
type NetEntry = { t: number; method: string; url: string; status: number; ms: number; body?: string; requestId?: string | null };

export const buffers = {
  crumbs: [] as Crumb[],
  errors: [] as ErrorEntry[],
  net: [] as NetEntry[],
  startedAt: Date.now(),
};

const push = <T,>(arr: T[], v: T, max = 50) => {
  arr.push(v);
  if (arr.length > max) arr.shift();
};

export function crumb(kind: string, detail: string) {
  push(buffers.crumbs, { t: Date.now(), kind, detail: Array.from(detail).slice(0, 200).join('') });
}

function describe(el: HTMLElement): string {
  const text = (el.innerText || el.getAttribute('aria-label') || el.getAttribute('title') || '').trim().replace(/\s+/g, ' ');
  const id = el.id ? `#${el.id}` : '';
  const cls = typeof el.className === 'string' && el.className ? `.${el.className.trim().split(/\s+/).slice(0, 2).join('.')}` : '';
  return `${el.tagName.toLowerCase()}${id}${cls} "${Array.from(text).slice(0, 60).join('')}"`;
}

let installed = false;
export function installRecorder() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (e) =>
    push(buffers.errors, { message: e.message, stack: e.error?.stack, source: `${e.filename}:${e.lineno}:${e.colno}` }, 20),
  );
  window.addEventListener('unhandledrejection', (e) => {
    const reason = (e as PromiseRejectionEvent).reason;
    push(buffers.errors, { message: `unhandledrejection: ${String(reason)}`, stack: (reason as Error)?.stack }, 20);
  });

  for (const level of ['error', 'warn'] as const) {
    const orig = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      const err = args.find((a) => a instanceof Error) as Error | undefined;
      push(buffers.errors, { level, message: Array.from(args.map(String).join(' ')).slice(0, 1000).join(''), stack: err?.stack }, 20);
      orig(...args);
    };
  }

  const origFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const started = Date.now();
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const method = init?.method ?? (input instanceof Request ? input.method : 'GET');
    if (!url.includes('/api/feedback')) crumb('fetch', `${method} ${url}`);
    try {
      const res = await origFetch(input, init);
      if (!res.ok) {
        const body = await res.clone().text().catch(() => '');
        push(
          buffers.net,
          { t: started, method, url, status: res.status, ms: Date.now() - started, body: Array.from(body).slice(0, 500).join(''), requestId: res.headers.get('x-request-id') },
          20,
        );
      }
      return res;
    } catch (err) {
      push(buffers.net, { t: started, method, url, status: 0, ms: Date.now() - started, body: String(err) }, 20);
      throw err;
    }
  };

  document.addEventListener(
    'click',
    (e) => {
      const el = (e.target as HTMLElement | null)?.closest?.('button,a,[role="button"],[role="tab"],summary,label') as HTMLElement | null;
      if (el) crumb('click', describe(el));
    },
    { capture: true },
  );
  document.addEventListener(
    'submit',
    (e) => {
      const f = e.target as HTMLFormElement;
      crumb('submit', `form${f.getAttribute('aria-label') ? ` "${f.getAttribute('aria-label')}"` : ''}`);
    },
    { capture: true },
  );
}

const SECRET = /token|password|secret|authorization|cookie|api[-_]?key|bearer/i;
export function redact<T>(value: T, depth = 0): T {
  if (depth > 6 || value == null) return value;
  if (Array.isArray(value)) return value.map((v) => redact(v, depth + 1)) as unknown as T;
  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, SECRET.test(k) ? '[redacted]' : redact(v, depth + 1)]),
    ) as T;
  }
  return value;
}
